import React, { Component, createRef } from "react";
import D3Chart from "./D3Chart";
import ModalWrapper from "./ModalWrapper";

/**
 * ChartWrapper — wraps the D3 chart and exposes an imperative handle
 * (via forwardRef) so the parent can ask for a native full-size SVG
 * clone for PDF capture.
 */
const ChartWrapperInner = (props, ref) => {
  const chartHostRef = createRef();
  const d3ChartRef = React.useRef(null);
  const [modal, setModal] = React.useState({ showModal: false, dataOnModal: "" });

  const callback = React.useCallback((childData) => {
    setModal({
      showModal: childData.showModal,
      dataOnModal: childData.dataOnModal,
    });
  }, []);

  React.useEffect(() => {
    d3ChartRef.current = new D3Chart(chartHostRef.current, callback);
  }, [callback]);

  // Expose imperative API to the parent.
  React.useImperativeHandle(ref, () => ({
    /**
     * Returns a detached <svg> node containing the FULL tree at native
     * 3000×2000 resolution (i.e. NOT clipped by the current zoom/pan).
     * Used by PdfDownloadComponent for full-tree capture.
     */
    getFullSizeSVG: () => {
      return d3ChartRef.current?.exportFullSizeSVG() ?? null;
    },
  }));

  const zoomIn   = () => d3ChartRef.current?.zoomBy(1.4);
  const zoomOut  = () => d3ChartRef.current?.zoomBy(1 / 1.4);
  const zoomReset = () => d3ChartRef.current?.reset();

  return (
    <div className="chart-panel">
      <div className="chart-toolbar">
        <span>Scroll to zoom · Drag to pan · Click a node for details</span>
        <div className="zoom-controls">
          <button className="zoom-btn" onClick={zoomOut} aria-label="Zoom out">−</button>
          <button className="zoom-btn" onClick={zoomReset}>Reset</button>
          <button className="zoom-btn" onClick={zoomIn} aria-label="Zoom in">+</button>
        </div>
      </div>

      <ModalWrapper
        showModal={modal.showModal}
        dataOnModal={modal.dataOnModal}
      />

      <div className="chart-canvas" ref={chartHostRef} />
    </div>
  );
};

const ChartWrapper = React.forwardRef(ChartWrapperInner);
export default ChartWrapper;
